import { squadChain, type Squad, type SquadDirectory, type SquadRole } from '@tive/core';

/** How a role reads in a picker, where there is room for the whole word. */
export const SQUAD_ROLE_LABELS: Record<SquadRole, string> = {
  lead: 'Lead',
  product: 'Product',
  design: 'Design',
  engineer: 'Engineer',
  member: 'Member',
};

/**
 * How a role reads on a team box, where the space is a badge. Plain members get
 * nothing: they are the default, and a row of "MEMBER" badges is noise that
 * hides the one person who is the lead.
 */
export function squadRoleBadge(role: SquadRole): string {
  if (role === 'member') return '';
  return role === 'engineer' ? 'Eng' : SQUAD_ROLE_LABELS[role];
}

/** The teams one person is on, with the hat they wear on each, in squad order. */
export function membershipsFor(
  directory: SquadDirectory,
  personId: string,
): { squad: Squad; role: SquadRole }[] {
  const out: { squad: Squad; role: SquadRole }[] = [];
  for (const squad of directory.squads) {
    const member = squad.members.find((candidate) => candidate.personId === personId);
    if (member) out.push({ squad, role: member.role });
  }
  return out;
}

/**
 * What is being dragged around the team board.
 *
 * Both kinds land on the same target — a team card — so they are one type
 * rather than two drag mechanisms that happen to overlap on the drop.
 */
export type BoardDrag =
  | { kind: 'squad'; squadId: string; name: string }
  | { kind: 'member'; personId: string; name: string; fromSquadId: string; role: SquadRole };

/**
 * Whether a drag may be dropped on a team, and why not when it may not.
 *
 * A null reason means "not a target, and nothing to explain" — dropping a team
 * back where it already is, or a person back on the team they came from. Those
 * are refused rather than allowed as no-ops, because in a plan a no-op write
 * still leaves a change entry saying nothing happened.
 */
export function canDropOnSquad(
  directory: SquadDirectory,
  drag: BoardDrag,
  targetId: string,
): { ok: boolean; reason: string | null } {
  if (drag.kind === 'member') {
    if (drag.fromSquadId === targetId) return { ok: false, reason: null };
    return { ok: true, reason: null };
  }

  if (drag.squadId === targetId) return { ok: false, reason: 'A team cannot go inside itself.' };

  const target = directory.squads.find((squad) => squad.id === targetId);
  const dragged = directory.squads.find((squad) => squad.id === drag.squadId);
  if (!target || !dragged) return { ok: false, reason: null };

  /*
   * The loop first, then the no-op, because a team dropped on its own sub-team
   * is both — and of the two answers, "you are trying to make a loop" is the
   * one worth saying. Inside its own sub-team has no meaning, and the
   * guardrails in core refuse it anyway; offering the drop only to have it
   * bounce is worse than not offering it.
   */
  const inside = squadChain(directory, targetId).some((step) => step.id === drag.squadId);
  if (inside) return { ok: false, reason: `${target.name} is already inside ${drag.name}.` };

  // The dragged team's parent, not the target's: back where it already is.
  if (dragged.parentId === targetId) return { ok: false, reason: null };

  return { ok: true, reason: null };
}
